import { Spacing } from '@persona-id/design-tokens';
import { FilterIcon } from '@persona-id/design-tokens/icons';
import _ from 'lodash';
import { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import { VerificationTemplateFilter } from 'dashboard/components/VerificationAnalytics';
import DateGranularitySelector from 'dashboard/components/VerificationAnalytics/DateGranularitySelector';
import { TableNullState } from 'dashboard/layouts/TableLayout';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { Period, UnixTime } from 'dashboard/lib/unixTime';
import { fillChartData, generateDomain, getEffectiveInterval } from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHour,
  formatChartDateHourChart,
  formatChartDateLong,
  formatChartDateLongWithDay,
  formatChartDateMonth,
  formatChartMonthName,
} from 'dashboard/lib/utils/formatters';
import { CheckName, CheckStatus } from 'dashboard/models/check';
import {
  MetricsVerificationChecksOverTimeDatum,
  VerificationCheckRateDatum,
  VerificationCheckV2Metrics,
  VerificationCheckV2TypeDatum,
} from 'dashboard/models/metrics';
import { DynamicFilters, MetricsGroupBy } from 'dashboard/models/metrics-verification';
import { Verification } from 'dashboard/models/verification';
import {
  FilterRulesForType,
  VerificationMetricsState,
  VerificationTemplatesGroupedByType,
} from 'dashboard/redux/slices/metrics-verification';
import ChecksLineChartV2 from 'dashboard/views/DashboardVerificationAnalyticsChecksV2View/ChecksLineChartV2';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import { ORDERED_VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/DashboardVerificationAnalyticsVolumeView';
import FilterPanel from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/FilterPanel';
import {
  FilterDimensions,
  FilteredVerificationType,
  PERIODICTY_MAP,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { ApiModel, VerificationType } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

const VerificationMetricsContainer = styled.section`
  font-family: ${({ theme }) => theme.text.fontFamily.base};
`;

// Height is chart height + card padding
const VerificationComposedCardBody = styled(Card.Body)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 0px;
`;

const SectionTitle = styled(Card.Title)`
  min-height: ${Spacing[7]};
  margin-right: -${Spacing[2]};
`;

const MetricsCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${Spacing[2]};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const VerificationMetricsSummaryContainer = styled.div`
  width: 100%;
  padding-left: ${Spacing[5]};
  padding-top: ${Spacing[1]};
  padding-right: ${Spacing[5]};
`;

// Leave a little more space on the right side, since the icon itself has built in padding left
const StyledButton = styled(Button)`
  padding: ${Spacing[2]} ${Spacing[3]} ${Spacing[2]} ${Spacing[2]};
  margin: ${Spacing[2]};
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: ${Spacing[5]};
  margin-left: auto;
  justify-content: right;
}
`;

const StyledFilterPanel = styled(FilterPanel)`
  position: absolute;
  overflow: visible;
`;

// The types that allow filtration + grouping
const FILTER_TYPES = [
  ApiModel.VerificationGovernmentId,
  ApiModel.VerificationSelfie,
  ApiModel.VerificationDatabase,
  ApiModel.VerificationAamva,
];
const REQUIRED_SUFFIX = '_required';
const NON_REQUIRED_SUFFIX = '_nonrequired';
const NUM_CHECKS_HIGHLIGHTED = 8;

// For the rare event that we don't have a check configured. This mainly happens in staging
function titleize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).split('_').join(' ');
}

function gatherCheckName(name: string) {
  return CHECK_DISPLAY_CONFIG[name as CheckName]
    ? CHECK_DISPLAY_CONFIG[name as CheckName].name
    : titleize(name) || '';
}

export interface CheckMetricsProps
  extends Pick<
    VerificationMetricsState,
    | 'verificationChecksOverTime'
    | 'lastQueryFilters'
    | 'filterRules'
    | 'checksLastQueryFilterRules'
    | 'verificationGranularities'
  > {
  queriedAt: Iso8601DateString;
  handleAddAndRule: (type: FilteredVerificationType, orRuleIndex: number) => void;
  handleUpdateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  handleDeleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  handleAddOrRule: (type: FilteredVerificationType) => void;
  handleLoadMetrics: () => void;
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  handleOpenFilters: (value: boolean) => void;
  handleVerificationTemplateFilterUpdate: (
    type: VerificationType,
    verificationTemplates: string[],
  ) => void;
  handleUpdateGranularity: (type: VerificationType, period: Period) => void;
  onDeepLink: (
    check: string,
    label: number,
    nameMap: Record<string, CheckName>,
    status: CheckStatus,
    groupByEnabled: boolean,
  ) => void;
  verificationTemplates: VerificationTemplatesGroupedByType;
  dynamicFilterOptions: DynamicFilters;
}

const CheckMetricsWithFilters = ({
  verificationChecksOverTime,
  lastQueryFilters,
  filterRules,
  handleAddAndRule,
  handleAddOrRule,
  handleDeleteAndRule,
  handleUpdateAndRule,
  handleUpdateGranularity,
  handleLoadMetrics,
  handleBulkRulesUpdate,
  checksLastQueryFilterRules,
  handleOpenFilters,
  onDeepLink,
  verificationTemplates,
  handleVerificationTemplateFilterUpdate,
  dynamicFilterOptions,
  verificationGranularities,
}: CheckMetricsProps) => {
  const [showFilterDropdown, setFilterDropdown] = useState<null | VerificationType>(null);
  const [isOutsideClickEnabled, setIsOutsideClickEnabled] = useState(true);

  return (
    <VerificationMetricsContainer data-test='verification-metrics__verification-checks-section'>
      {(lastQueryFilters?.verificationTypes.length === 0
        ? (Object.keys(ORDERED_VERIFICATION_TYPE_NAMES_SHORT) as VerificationType[])
        : (lastQueryFilters?.verificationTypes as VerificationType[])
      ).map((verificationType: VerificationType) => {
        const datum: MetricsVerificationChecksOverTimeDatum =
          verificationChecksOverTime.data.find((summary) => {
            return summary.type === verificationType;
          }) ||
          ({
            type: verificationType,
            data: [] as VerificationCheckV2TypeDatum[],
          } as MetricsVerificationChecksOverTimeDatum);

        const overallData: VerificationCheckRateDatum[] = [];
        const checksData = datum.data;
        const requiredChecks: Record<string, string> = {};
        const unrequiredChecks: Record<string, string> = {};
        const summaryData: Record<string, VerificationCheckV2Metrics> = {};
        const checksNameMap: Record<string, CheckName> = {};

        // Massage our data to make it such that we have all of the names of the Checks + Pass Rates
        for (let j = 0; j < checksData.length; j++) {
          const currentCheck = checksData[j];
          const currentCheckName = currentCheck.check_required
            ? currentCheck.name + REQUIRED_SUFFIX
            : currentCheck.name + NON_REQUIRED_SUFFIX;
          checksNameMap[currentCheckName] = currentCheck.name;
          const currentCheckData = currentCheck.data;

          // IMPORTANT NOTE:  Here, we're going to add some filtering that references the Front End Checks config as a source
          // of truth for which checks are going to belong to which category (User Action Required / Fraud). We'll remove it
          // from the BE querying in Airflow, since we have to work incredibly hard to go ahead and actually manually categorize
          // these into one category or another. When new ones are added, we'll never be able to keep up

          // When there's a single filter on the checkTypes, lets just simply skip any check that is NOT in the selected Category
          if (lastQueryFilters?.checkTypes.length === 1) {
            // Ensure its a check we know about
            if (CHECK_DISPLAY_CONFIG[currentCheck.name] == null) {
              continue;
            }
            // Ensure that it's of the selected type
            if (
              lastQueryFilters?.checkTypes[0] !== CHECK_DISPLAY_CONFIG[currentCheck.name].category
            ) {
              continue;
            }
          }

          // Characterize the checks as req'd or non req'd
          if (currentCheck.check_required) {
            requiredChecks[currentCheckName] = currentCheck.name;
          } else {
            unrequiredChecks[currentCheckName] = currentCheck.name;
          }

          // First, establish the original initial counts
          summaryData[currentCheckName] = {
            pass_count: 0,
            failure_count: 0,
          };

          for (let i = 0; i < currentCheck.data.length; i++) {
            const currentElement = currentCheckData[i];
            const timeIndex = overallData.findIndex((element) => {
              return element.created_timestamp === currentElement.created_timestamp;
            });

            // For this given data point, we'll go ahead and add it into the data
            const passCount = currentElement['pass_count'] ?? 0;
            const notApplicableCount = currentElement['not_applicable_count'] ?? 0;
            const failureCount = currentElement['failure_count'] ?? 0;
            summaryData[currentCheckName]['pass_count'] += passCount;
            summaryData[currentCheckName]['pass_count'] += notApplicableCount;
            summaryData[currentCheckName]['failure_count'] += failureCount;

            // If theres an existing element with the timestamp, add to the element
            if (timeIndex !== -1) {
              overallData[timeIndex] = {
                ...overallData[timeIndex],
                [currentCheckName]:
                  ((passCount + notApplicableCount) /
                    (passCount + failureCount + notApplicableCount)) *
                  100,
              };
            } else {
              // Otherwise append the current element
              overallData.push({
                created_timestamp: currentElement.created_timestamp,
                [currentCheckName]:
                  ((passCount + notApplicableCount) /
                    (passCount + failureCount + notApplicableCount)) *
                  100,
              });
            }
          }
        }

        // Get all of our keys
        const checkKeys: CheckName[] = Array.from(
          new Set(
            overallData
              .map((observation) => {
                return Object.keys(observation) as CheckName[];
              })
              .flat(),
          ),
        );

        // Create a base value object + remove the timestamp
        const baseValue: Partial<Record<CheckName | 'created_timestamp', 0>> = {};
        for (let i = 0; i < checkKeys.length; i++) {
          baseValue[checkKeys[i]] = 0;
        }
        delete baseValue['created_timestamp'];

        const lineKeys: Record<string, string> = _.keys(baseValue).reduce(function (r, key) {
          let checkName = null;
          if (requiredChecks[key]) {
            checkName = gatherCheckName(requiredChecks[key]);
          } else if (unrequiredChecks[key]) {
            checkName = gatherCheckName(unrequiredChecks[key]);
          } else {
            // bad things happen here
          }
          r[key] = checkName;
          return r;
        }, Object.create(null));

        // Now pop the Required Checks and Unrequired Checks into their places
        const lineGroups: Record<string, Record<string, string>> = {
          'Required checks': {},
          'Unrequired checks': {},
        };

        Object.keys(unrequiredChecks).forEach((key) => {
          lineGroups['Unrequired checks'][key] = lineKeys[key];
        });

        Object.keys(requiredChecks).forEach((key) => {
          lineGroups['Required checks'][key] = lineKeys[key];
        });

        const toolTipPeriod = verificationGranularities[verificationType];

        const filledData = fillChartData(
          overallData,
          baseValue,
          generateDomain(
            [
              // These are in MS, need to divide
              lastQueryFilters ? Date.parse(lastQueryFilters.createdAtStart) / 1000 : undefined,
              lastQueryFilters ? Date.parse(lastQueryFilters.createdAtEnd) / 1000 : undefined,
            ],
            verificationGranularities[verificationType],
          ),
          toolTipPeriod,
          true,
          true,
        );

        const createTooltipHeader = (label: UnixTime) => {
          return toolTipPeriod === 'week'
            ? `Week of ${formatChartDateLong(label)}`
            : toolTipPeriod === 'day'
              ? formatChartDateLongWithDay(label)
              : toolTipPeriod === 'month'
                ? formatChartDateMonth(label)
                : formatChartDateHour(label);
        };

        const sortedLineKeys = Object.keys(lineKeys).sort(function (a, b) {
          return lineKeys[a].localeCompare(lineKeys[b]);
        }) as CheckName[];

        const sortedRequiredChecks = Object.keys(requiredChecks).sort(function (a, b) {
          return gatherCheckName(requiredChecks[a]).localeCompare(
            gatherCheckName(requiredChecks[b]),
          );
        }) as CheckName[];

        const sortedUnrequiredChecks = Object.keys(unrequiredChecks).sort(function (a, b) {
          return gatherCheckName(unrequiredChecks[a]).localeCompare(
            gatherCheckName(unrequiredChecks[b]),
          );
        }) as CheckName[];

        // In order for us to have a matching initial order in the Expanded Legend, we'll need to sort our summary
        // data according to where it lies in
        const sortedSummaryKeys = Object.keys(summaryData).sort((first, second) => {
          let firstIndex = sortedRequiredChecks.findIndex((item) => item === first);
          if (firstIndex === -1) {
            firstIndex =
              sortedRequiredChecks.length +
              sortedUnrequiredChecks.findIndex((item) => item === first);
          }

          let secondIndex = sortedRequiredChecks.findIndex((item) => item === second);

          if (secondIndex === -1) {
            secondIndex =
              sortedRequiredChecks.length +
              sortedUnrequiredChecks.findIndex((item) => item === second);
          }

          return firstIndex > secondIndex ? 1 : -1;
        });

        const sortedSummaryData: Record<string, VerificationCheckV2Metrics> = {};
        sortedSummaryKeys.forEach((key) => {
          sortedSummaryData[key] = summaryData[key];
        });

        const verificationTemplatesOptionsForType = verificationTemplates[verificationType];
        // If there are options for this type, check if there's a none option and then append some copy to it
        if (verificationTemplatesOptionsForType != null) {
          const legacyTemplate = verificationTemplatesOptionsForType.find(
            (template) => template.id == null,
          );

          if (legacyTemplate != null) {
            legacyTemplate.tooltipText = 'Verifications that do not have an associated template';
          }
        }

        return (
          <MetricsCard key={datum.type}>
            {verificationType in filterRules && showFilterDropdown === verificationType && (
              <OutsideClickHandler
                onOutsideClick={() => {
                  setFilterDropdown(null);
                  handleOpenFilters(false);
                  handleBulkRulesUpdate(checksLastQueryFilterRules);
                }}
                display='contents'
                disabled={!isOutsideClickEnabled}
              >
                <StyledFilterPanel
                  panelTitle={`Edit Filters - ${
                    VERIFICATION_TYPE_NAMES_SHORT[verificationType as Verification['type']]
                  }`}
                  verificationType={verificationType as FilteredVerificationType}
                  addAndRule={handleAddAndRule}
                  addOrRule={handleAddOrRule}
                  deleteAndRule={handleDeleteAndRule}
                  updateAndRule={handleUpdateAndRule}
                  filterRules={
                    filterRules[verificationType as VerificationType].or as FilterRule[][]
                  }
                  handleCloseDropdown={() => setIsOutsideClickEnabled(true)}
                  handleOpenDropdown={() => setIsOutsideClickEnabled(false)}
                  handleLoadMetrics={handleLoadMetrics}
                  handleCancel={() => {
                    setFilterDropdown(null);
                    handleOpenFilters(false);
                  }}
                  handleBulkRulesUpdate={handleBulkRulesUpdate}
                  lastQueryFilterRules={checksLastQueryFilterRules}
                  typeDynamicOptions={
                    dynamicFilterOptions[verificationType as FilteredVerificationType]
                  }
                />
              </OutsideClickHandler>
            )}
            <VerificationMetricsSummaryContainer>
              <HeaderContainer>
                <SectionTitle data-test='verification-type-metrics-card--name'>
                  {VERIFICATION_TYPE_NAMES_SHORT[datum.type as Verification['type']]}
                </SectionTitle>
                <OptionsContainer>
                  {sortedLineKeys.length > 0 && (
                    <DateGranularitySelector
                      value={verificationGranularities[verificationType]}
                      verificationType={verificationType}
                      handleUpdateGranularity={handleUpdateGranularity}
                      dateRange={[lastQueryFilters?.createdAtStart, lastQueryFilters?.createdAtEnd]}
                    />
                  )}
                  {verificationTemplatesOptionsForType && (
                    <VerificationTemplateFilter
                      verificationTemplateFilterRules={
                        filterRules[verificationType].verificationTemplateIds
                      }
                      verificationType={verificationType}
                      verificationTemplates={verificationTemplatesOptionsForType}
                      handleVerificationTemplateFilterUpdate={
                        handleVerificationTemplateFilterUpdate
                      }
                    />
                  )}
                  {FILTER_TYPES.includes(verificationType) && (
                    <StyledButton
                      id={`verification-filters-${verificationType}`}
                      type='button'
                      variant='basic'
                      width='auto'
                      prefixIcon={<FilterIcon />}
                      onClick={() => {
                        setFilterDropdown(verificationType);
                        handleOpenFilters(true);
                      }}
                    >
                      Filters{' '}
                      {filterRules[verificationType as FilteredVerificationType].or.length > 0 &&
                      // Ensure it doesn't have a list of lists with only a single inner empty object
                      !(
                        (filterRules[verificationType as FilteredVerificationType].or.length ===
                          1 &&
                          filterRules[verificationType as FilteredVerificationType].or[0].length ===
                            1 &&
                          Object.keys(
                            filterRules[verificationType as FilteredVerificationType].or[0][0],
                          ).length === 0) ||
                        // Case when its empty
                        (filterRules[verificationType as FilteredVerificationType].or.length ===
                          1 &&
                          filterRules[verificationType as FilteredVerificationType].or[0].length ===
                            0)
                      )
                        ? `(${filterRules[verificationType as FilteredVerificationType].or.length})`
                        : ''}
                    </StyledButton>
                  )}
                </OptionsContainer>
              </HeaderContainer>
            </VerificationMetricsSummaryContainer>
            <VerificationComposedCardBody>
              <Card.Subtitle className='verification-summary__status-subtitle'>
                Pass Rates ({PERIODICTY_MAP[toolTipPeriod]})
              </Card.Subtitle>
              {sortedLineKeys.length === 0 ? (
                <TableNullState hasUsedFilters={true} dataTestBlock='checks-view'>
                  <div>No matched results. Try adjusting your filters to view data.</div>
                </TableNullState>
              ) : (
                <ChecksLineChartV2
                  {...verificationChecksOverTime}
                  data={filledData}
                  rawChecksData={checksData}
                  summaryData={sortedSummaryData}
                  lineKeys={sortedLineKeys}
                  lineGroups={lineGroups}
                  requiredChecks={requiredChecks}
                  unrequiredChecks={unrequiredChecks}
                  createTooltipHeader={createTooltipHeader}
                  barIsAnimationActive={false}
                  legendFormatter={(value: any) => {
                    return lineKeys[value as CheckName];
                  }}
                  xDataKey='created_timestamp'
                  xFormatter={
                    toolTipPeriod === Period.Hour &&
                    toolTipPeriod === verificationChecksOverTime.period
                      ? formatChartDateHourChart
                      : toolTipPeriod === Period.Month
                        ? formatChartMonthName
                        : formatChartDate
                  }
                  initialFilteredLegendKeys={
                    sortedRequiredChecks.length > 0
                      ? sortedRequiredChecks.slice(0, NUM_CHECKS_HIGHLIGHTED)
                      : sortedUnrequiredChecks.slice(0, NUM_CHECKS_HIGHLIGHTED)
                  }
                  onDeepLink={onDeepLink}
                  checkNameMap={checksNameMap}
                  chartPeriod={toolTipPeriod}
                  interval={getEffectiveInterval(verificationChecksOverTime.period, toolTipPeriod)}
                  groupedByInquiries={lastQueryFilters?.groupBy === MetricsGroupBy.Inquiry}
                />
              )}
            </VerificationComposedCardBody>
          </MetricsCard>
        );
      })}
    </VerificationMetricsContainer>
  );
};

export default CheckMetricsWithFilters;
